/**
 * Session activity tracking for idle timeout + absolute session cap.
 *
 * Keeps the user logged in while they are actively working (so token refresh
 * on 401 / near-expiry is allowed), but forces logout after IDLE_MS with no
 * real user interaction, or ABSOLUTE_MS after login regardless of activity.
 *
 * IMPORTANT: only DOM user input (pointerdown / keydown / touchstart) counts as
 * activity. Background polling, API responses, and Firebase onIdTokenChanged
 * must NOT call recordUserActivity() — otherwise the session never idles out.
 *
 * Mirror of dashboard/src/utils/sessionActivity.ts (kept in sync by hand; there
 * is no shared frontend package).
 */

/** No user interaction for this long → forced logout. */
export const IDLE_MS = 60 * 60 * 1000; // 60 minutes

/** Hard cap from login → logout even if the user is still active. */
export const ABSOLUTE_MS = 12 * 60 * 60 * 1000; // 12 hours

const LAST_ACTIVITY_KEY = 'session_last_activity_at';
const SESSION_START_KEY = 'session_started_at';

/** Throttle sessionStorage writes so high-frequency input is cheap. */
const RECORD_THROTTLE_MS = 1000;
let lastRecordedAt = 0;

function now(): number {
  return Date.now();
}

function readNumber(key: string): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

function writeNumber(key: string, value: number): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(key, String(value));
  } catch {
    /* sessionStorage unavailable — degrade to no idle enforcement */
  }
}

/** Call on successful login: (re)start the absolute-cap clock and mark active. */
export function markSessionStart(): void {
  const ts = now();
  writeNumber(SESSION_START_KEY, ts);
  writeNumber(LAST_ACTIVITY_KEY, ts);
  lastRecordedAt = ts;
}

/** Record a real user interaction. Throttled. Safe to call very frequently. */
export function recordUserActivity(): void {
  const ts = now();
  if (ts - lastRecordedAt < RECORD_THROTTLE_MS) return;
  lastRecordedAt = ts;
  writeNumber(LAST_ACTIVITY_KEY, ts);
}

export function getLastActivity(): number | null {
  return readNumber(LAST_ACTIVITY_KEY);
}

export function getSessionStart(): number | null {
  return readNumber(SESSION_START_KEY);
}

/** True when idle past IDLE_MS. Unknown last-activity is treated as NOT idle. */
export function isIdle(idleMs: number = IDLE_MS): boolean {
  const last = getLastActivity();
  if (last == null) return false;
  return now() - last >= idleMs;
}

/** True when the session is older than ABSOLUTE_MS. Unknown start = NOT expired. */
export function isSessionExpired(absoluteMs: number = ABSOLUTE_MS): boolean {
  const start = getSessionStart();
  if (start == null) return false;
  return now() - start >= absoluteMs;
}

/**
 * Whether a token refresh should be allowed right now. Refresh is blocked once
 * the user is idle or the absolute cap is reached — this is what stops
 * background polls from silently keeping a dead session alive.
 */
export function shouldAllowTokenRefresh(): boolean {
  return !isIdle() && !isSessionExpired();
}

/** Reason the session should end, or null if it may continue. */
export function forcedLogoutReason(): 'idle_timeout' | 'session_expired' | null {
  if (isSessionExpired()) return 'session_expired';
  if (isIdle()) return 'idle_timeout';
  return null;
}

/** True when the idle timer / guards should force a logout. */
export function shouldForceLogout(): boolean {
  return forcedLogoutReason() !== null;
}

/** Clear activity state on logout. */
export function clearSessionActivity(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(LAST_ACTIVITY_KEY);
    window.sessionStorage.removeItem(SESSION_START_KEY);
  } catch {
    /* ignore */
  }
  lastRecordedAt = 0;
}

const ACTIVITY_EVENTS: Array<keyof DocumentEventMap> = [
  'pointerdown',
  'keydown',
  'touchstart',
];

/**
 * Attach user-activity listeners to `root` (default: document). Returns an
 * uninstall function. Only real DOM input is wired — not scroll / focus /
 * visibilitychange, which can fire without the user actually working.
 *
 * `onActivity` runs on every such input (unthrottled) — used by the activity
 * ping that keeps the scale-to-zero services warm.
 */
export function installUserActivityListeners(
  root: Document | HTMLElement = typeof document !== 'undefined'
    ? document
    : (undefined as unknown as Document),
  onActivity?: () => void,
): () => void {
  if (!root || typeof root.addEventListener !== 'function') {
    return () => undefined;
  }
  const handler = () => {
    recordUserActivity();
    onActivity?.();
  };
  for (const event of ACTIVITY_EVENTS) {
    root.addEventListener(event, handler, { passive: true, capture: true });
  }
  return () => {
    for (const event of ACTIVITY_EVENTS) {
      root.removeEventListener(event, handler, { capture: true } as EventListenerOptions);
    }
  };
}
